/**
 * Country Selection Store - Zustand Implementation
 *
 * Manages the customer's country selection globally.
 * Persists to localStorage for consistent experience across sessions.
 *
 * Business Rule: Never auto-detect country from browser.
 * Customer MUST explicitly select their country.
 *
 * @module features/country/hooks/useCountryStore
 */

import {create} from 'zustand';
import {devtools, persist} from 'zustand/middleware';
import type {CountryDetectionStatus, CountrySource, CountryStore, DetectedCountryPayload,} from '../types';
import { getCurrencyForCountry, getFlagForCountry } from '../api/countryData';

/** LocalStorage key for country persistence */
const STORAGE_KEY = 'tangi_country_selection';

// Note: Country-to-currency mapping is now fetched from API via countryData.ts
// The getCurrencyForCountry function is imported from '../api/countryData'

const DEFAULT_STATE = {
  countryCode: null,
  countryName: null,
  currency: null,
  hasExplicitSelection: false,
  countrySource: 'auto' as CountrySource,
  showCountryModal: false,
  lastVerifiedCountryCode: null,
  autoDetectedCountryCode: null,
  autoDetectedCountryName: null,
  autoDetectedCurrency: null,
  autoDetectedAt: null,
  detectionStatus: 'idle' as CountryDetectionStatus,
  detectionError: null as string | null,
};

/**
 * Country Selection Store
 *
 * Manages:
 * - Selected country code
 * - Derived currency
 * - Explicit selection flag
 * - Country selector modal state
 */
export const useCountryStore = create<CountryStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...DEFAULT_STATE,

        // Actions
        setCountry: (
          code: string,
          options?: { source?: CountrySource; name?: string | null; currency?: string | null }
        ): void => {
          const upperCode = code.toUpperCase();
          const resolvedCurrency = options?.currency ?? getCurrencyForCountry(upperCode);
          set(
            {
              countryCode: upperCode,
              countryName: options?.name ?? get().countryName,
              currency: resolvedCurrency,
              hasExplicitSelection: true,
              countrySource: options?.source ?? 'manual',
              showCountryModal: false,
              detectionError: null,
            },
            false,
            'setCountry'
          );
        },

        setCheckoutCountry: (code: string): void => {
          const upperCode = code.toUpperCase();
          const currency = getCurrencyForCountry(upperCode);
          set(
            {
              countryCode: upperCode,
              currency,
              hasExplicitSelection: true,
              countrySource: 'checkout',
              lastVerifiedCountryCode: upperCode,
              showCountryModal: false,
            },
            false,
            'setCheckoutCountry'
          );
        },

        clearCountry: (): void => {
          set(
            {
              ...DEFAULT_STATE,
              autoDetectedCountryCode: get().autoDetectedCountryCode,
              autoDetectedCountryName: get().autoDetectedCountryName,
              autoDetectedCurrency: get().autoDetectedCurrency,
              autoDetectedAt: get().autoDetectedAt,
              detectionStatus: get().detectionStatus,
              detectionError: get().detectionError,
            },
            false,
            'clearCountry'
          );
        },

        openCountryModal: (): void => {
          set({ showCountryModal: true }, false, 'openCountryModal');
        },

        closeCountryModal: (): void => {
          set({ showCountryModal: false }, false, 'closeCountryModal');
        },

        initializeFromStorage: (): void => {
          // Nothing to do — state is already restored via persist middleware.
        },

        setDetectionPending: (): void => {
          set(
            {
              detectionStatus: 'pending',
              detectionError: null,
            },
            false,
            'setDetectionPending'
          );
        },

        setDetectionError: (message: string): void => {
          set(
            {
              detectionStatus: 'error',
              detectionError: message,
            },
            false,
            'setDetectionError'
          );
        },

        applyAutoDetectedCountry: (payload: DetectedCountryPayload): void => {
          const upperCode = payload.countryCode.toUpperCase();
          if (!upperCode) {
            set(
              {
                detectionStatus: 'error',
                detectionError: 'Invalid country data from detector',
              },
              false,
              'applyAutoDetectedCountry'
            );
            return;
          }

          const resolvedCurrency =
            payload.currencyCode ?? getCurrencyForCountry(upperCode);

          set(
            (state) => {
              const shouldApplySelection =
                !state.countryCode || state.countrySource === 'auto';

              return {
                autoDetectedCountryCode: upperCode,
                autoDetectedCountryName: payload.countryName ?? state.autoDetectedCountryName,
                autoDetectedCurrency: resolvedCurrency,
                autoDetectedAt: Date.now(),
                detectionStatus: 'success',
                detectionError: null,
                ...(shouldApplySelection
                  ? {
                      countryCode: upperCode,
                      countryName: payload.countryName ?? state.countryName,
                      currency: resolvedCurrency,
                      hasExplicitSelection: true,
                      countrySource: 'auto' as CountrySource,
                    }
                  : {}),
              };
            },
            false,
            'applyAutoDetectedCountry'
          );
        },
      }),
      {
        name: STORAGE_KEY,
        // Only persist these fields
        partialize: (state) => ({
          countryCode: state.countryCode,
          countryName: state.countryName,
          currency: state.currency,
          hasExplicitSelection: state.hasExplicitSelection,
          countrySource: state.countrySource,
          lastVerifiedCountryCode: state.lastVerifiedCountryCode,
        }),
      }
    ),
    {
      name: 'country-store',
    }
  )
);

// ============================================
// CONVENIENCE HOOKS
// ============================================

/**
 * Hook to get current country code
 */
export function useCountryCode(): string | null {
  return useCountryStore((state) => state.countryCode);
}

export function useCountryName(): string | null {
  return useCountryStore((state) => state.countryName);
}

/**
 * Hook to get current currency code
 */
export function useCurrency(): string | null {
  return useCountryStore((state) => state.currency);
}

/**
 * Hook to check if user has made explicit selection
 */
export function useHasExplicitSelection(): boolean {
  return useCountryStore((state) => state.hasExplicitSelection);
}

export function useCountrySource(): CountrySource {
  return useCountryStore((state) => state.countrySource);
}

export function useCountryDetectionStatus(): CountryDetectionStatus {
  return useCountryStore((state) => state.detectionStatus);
}

export function useCountryDetectionError(): string | null {
  return useCountryStore((state) => state.detectionError);
}

export function useAutoDetectedCountry(): string | null {
  return useCountryStore((state) => state.autoDetectedCountryCode);
}

/**
 * Hook to check if country modal should be shown
 */
export function useShowCountryModal(): boolean {
  return useCountryStore((state) => state.showCountryModal);
}

/**
 * Hook to get setCountry action
 */
export function useSetCountry(): (
  code: string,
  options?: { source?: CountrySource; name?: string | null; currency?: string | null }
) => void {
  return useCountryStore((state) => state.setCountry);
}

/**
 * Hook to get modal control actions
 */
export function useCountryModalActions(): {
  open: () => void;
  close: () => void;
} {
  const open = useCountryStore((state) => state.openCountryModal);
  const close = useCountryStore((state) => state.closeCountryModal);
  return { open, close };
}

// Note: Flag emoji lookup is now handled by getFlagForCountry from '../api/countryData'

/**
 * Hook to get country display info
 * Returns formatted display like "🇸🇪 SEK" or null if not selected
 */
export function useCountryDisplay(): string | null {
  const countryCode = useCountryStore((state) => state.countryCode);
  const countryName = useCountryStore((state) => state.countryName);
  const currency = useCountryStore((state) => state.currency);

  if (!countryCode || !currency) return null;

  const flag = getFlagForCountry(countryCode);
  const name = countryName ?? countryCode;
  return `${flag} ${name} · ${currency}`;
}

// ============================================
// API INTEGRATION HOOKS
// ============================================

/**
 * Hook to get country code and currency for API calls
 *
 * This replaces the legacy `locale-persistence` helpers.
 * Use this hook in checkout, product, and pricing API calls.
 *
 * @returns Object with countryCode, currency, and ready flag
 *
 * @example
 * ```tsx
 * const { countryCode, currency, isReady } = useCountryForAPI();
 *
 * // Use in API call
 * const pricing = await fetchPricing({ country_code: countryCode, currency });
 * ```
 */
export function useCountryForAPI(): {
  countryCode: string | null;
  currency: string | null;
  isReady: boolean;
} {
  const countryCode = useCountryStore((state) => state.countryCode);
  const currency = useCountryStore((state) => state.currency);

  return {
    countryCode,
    currency,
    isReady: !!countryCode && !!currency,
  };
}

/**
 * Get country code and currency synchronously (non-reactive)
 *
 * Use this in non-React contexts like services or utilities.
 * For React components, prefer `useCountryForAPI()` hook.
 *
 * @returns Object with countryCode, currency, and ready flag
 */
export function getCountryForAPI(): {
  countryCode: string | null;
  currency: string | null;
  isReady: boolean;
} {
  const state = useCountryStore.getState();
  return {
    countryCode: state.countryCode,
    currency: state.currency,
    isReady: !!state.countryCode && !!state.currency,
  };
}
